/**
 * Service pour la gestion des utilisateurs
 * @module admin/services/usersService
 */

import { apiGet, apiPost, apiPut, apiPatch, apiDelete } from '../../../utils/api';

/**
 * Récupère la liste des utilisateurs avec pagination et filtres
 * @param {Object} params - Paramètres de pagination et filtres
 * @param {number} params.page - Numéro de page
 * @param {number} params.limit - Nombre d'éléments par page
 * @param {string} [params.search] - Recherche textuelle
 * @param {string} [params.role] - Filtre par rôle
 * @param {string} [params.isActive] - Filtre par statut actif
 * @returns {Promise<Object>} Liste des utilisateurs avec pagination
 * @throws {Error} Si la récupération échoue
 */
export const getUsers = async (params) => {
  const queryParams = new URLSearchParams({
    page: params.page,
    limit: params.limit,
    ...(params.search && { search: params.search }),
    ...(params.role && { role: params.role }),
    ...(params.isActive !== '' && { isActive: params.isActive }),
  });
  return await apiGet(`/admin/users?${queryParams.toString()}`);
};

/**
 * Crée un nouvel utilisateur
 * @param {Object} userData - Données du nouvel utilisateur
 * @param {string} userData.username - Nom d'utilisateur (requis)
 * @param {string} userData.password - Mot de passe (min 6 caractères)
 * @param {string} [userData.email] - Email (optionnel)
 * @param {string} [userData.name] - Nom affiché (optionnel)
 * @param {string} [userData.role='viewer'] - Rôle (viewer, moderator, admin)
 * @param {boolean} [userData.isActive=true] - Statut actif
 * @returns {Promise<Object>} Utilisateur créé
 * @throws {Error} Si la création échoue
 */
export const createUser = async (userData) => {
  return await apiPost('/admin/users', userData);
};

/**
 * Récupère les statistiques globales des utilisateurs
 * @returns {Promise<Object>} Statistiques (total, active, inactive, withTwitch, recent)
 * @throws {Error} Si la récupération échoue
 */
export const getUsersStats = async () => {
  return await apiGet('/admin/users/stats/overview');
};

/**
 * Met à jour un utilisateur
 * @param {string} userId - ID de l'utilisateur
 * @param {Object} userData - Données à mettre à jour
 * @returns {Promise<Object>} Résultat de la mise à jour
 * @throws {Error} Si la mise à jour échoue
 */
export const updateUser = async (userId, userData) => {
  return await apiPut(`/admin/users/${userId}`, userData);
};

/**
 * Supprime ou désactive un utilisateur
 * @param {string} userId - ID de l'utilisateur
 * @param {boolean} hardDelete - Si true, suppression définitive
 * @returns {Promise<Object>} Résultat de la suppression
 * @throws {Error} Si la suppression échoue
 */
export const deleteUser = async (userId, hardDelete = false) => {
  const params = hardDelete ? '?hardDelete=true' : '';
  return await apiDelete(`/admin/users/${userId}${params}`);
};

/**
 * Suppression définitive RGPD de plusieurs utilisateurs (admin)
 * @param {number[]} userIds - Identifiants (max 50)
 * @returns {Promise<Object>} deleted / failed
 */
export const bulkDeleteUsers = async (userIds) => {
  return await apiPost('/admin/users/bulk-delete', { userIds });
};

/**
 * Réactive un utilisateur
 * @param {string} userId - ID de l'utilisateur
 * @returns {Promise<Object>} Résultat de la réactivation
 * @throws {Error} Si la réactivation échoue
 */
export const activateUser = async (userId) => {
  return await apiPost(`/admin/users/${userId}/activate`);
};

/**
 * Change le mot de passe d'un utilisateur (admin uniquement)
 * @param {string} userId - ID de l'utilisateur
 * @param {string} password - Nouveau mot de passe (min 6 caractères)
 * @returns {Promise<Object>} Résultat de la modification
 * @throws {Error} Si la modification échoue
 */
export const changeUserPassword = async (userId, password) => {
  return await apiPatch(`/admin/users/${userId}/password`, { password });
};
